// Unified Review Workflow Integration — role/lifecycle gating selectors (pure).
//
// Pure, side-effect-free helpers (no network, no DOM, no React) that decide
// whether the current user may interact with the integration's new
// state-changing controls — "Generate Business Review" and "Share". They
// mirror, and never replace, the backend's authorization: the UI only
// hides/disables controls the server would reject, and defaults *closed* while
// the user's role is still unresolved (Req 9.5).
//
// Role membership is delegated to the shared `isVcioPlus` utility from
// `@shared/roleHierarchy` so the vCIO+ vocabulary (`vcio`, `admin`, `owner`)
// stays single-sourced across the client, consistent with the parallel
// `@/lib/unifiedReview/gating.ts`.
//
// References: design.md "Pure selectors (client/src/lib/reviewWorkflow) →
// gating.ts"; requirements 1.5, 2.7, 6.1, 6.3, 6.8, 7.2, 7.7, 7.8, 9.1, 9.2,
// 9.5, 9.6.

import { isVcioPlus as sharedIsVcioPlus } from "@shared/roleHierarchy";
import type { User } from "@shared/schema";
import type { CycleStatus } from "./routing";

/**
 * The authenticated user shape consumed by the gating selectors. This mirrors
 * the value returned by `useAuth().user` (`User | null` from `@shared/schema`).
 * Only the `role` field is read by these helpers, but the full type is used so
 * call sites can pass the `useAuth` user directly without narrowing.
 */
export type AuthUser = User;

/**
 * True when `user` is a vCIO+ user — their resolved role is one of `vcio`,
 * `admin`, or `owner`. Returns `false` whenever the role is unresolved
 * (`null`/`undefined`), so every gated control defaults closed while `useAuth`
 * is still loading (Req 9.5, 9.6). Delegates to the shared `isVcioPlus`
 * utility from `@shared/roleHierarchy` so the role vocabulary stays
 * single-sourced (Req 7.7).
 */
export function isVcioPlus(user: AuthUser | null | undefined): boolean {
  return sharedIsVcioPlus(user);
}

/**
 * Whether the "Generate Business Review" control should be enabled for `user`
 * on a cycle with the given `cycleStatus`.
 *
 * Enabled if and only if the user is vCIO+ AND the cycle is `completed`
 * (Req 6.1). For any other status the control is presented disabled (Req 6.3),
 * and an Alignment_Engineer (or unresolved role) never sees it enabled
 * (Req 6.8, 9.2).
 */
export function canGenerateQbr(
  user: AuthUser | null | undefined,
  cycleStatus: CycleStatus,
): boolean {
  return isVcioPlus(user) && cycleStatus === "completed";
}

/**
 * Whether the standalone "Share" control should be enabled for `user` on a
 * cycle with the given `cycleStatus`.
 *
 * Enabled if and only if the user is vCIO+ AND the cycle is `completed`
 * (Req 7.2). Sharing is independent of any QBR meeting; an Alignment_Engineer
 * (or unresolved role) never sees it enabled (Req 7.8, 9.2).
 */
export function canShare(
  user: AuthUser | null | undefined,
  cycleStatus: CycleStatus,
): boolean {
  return isVcioPlus(user) && cycleStatus === "completed";
}
